import React from 'react';
import { Pagination } from '../../../components/common/Pagination';
import { formatDecimal } from '../../../utils/format';

// The shared vocabulary for report cards. Sections on the same screen render
// through these so they stay consistent by construction rather than by
// copy-paste that drifts apart.

export const CARD_PAGE_SIZE = 10;

export const thBase = 'px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500';

interface ReportCardProps {
  icon: React.ReactNode;
  title: string;
  count: number;
  page: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Optional right-aligned note in the header, e.g. an as-of date. */
  note?: string;
  testId?: string;
  children: React.ReactNode;
}

// Icon header, count pill, scrollable body, and a page-10 footer that only
// appears once there is more than one page.
export const ReportCard: React.FC<ReportCardProps> = ({
  icon,
  title,
  count,
  page,
  total,
  onPageChange,
  note,
  testId,
  children,
}) => (
  <div className="flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm" data-test-id={testId}>
    <header className="flex items-center gap-3 px-5 py-4">
      <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-primary-50 text-primary-600">
        {icon}
      </span>
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <span className="ml-auto rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium tabular-nums text-gray-600">
        {formatDecimal(count, 0)}
      </span>
      {note && <span className="text-xs text-gray-500">{note}</span>}
    </header>
    <div className="overflow-x-auto border-t border-gray-100">{children}</div>
    {total > CARD_PAGE_SIZE && (
      <Pagination
        page={page}
        pageSize={CARD_PAGE_SIZE}
        total={total}
        onPageChange={onPageChange}
        compact
        className="mt-auto rounded-b-xl"
      />
    )}
  </div>
);

export const EmptyState: React.FC<{ icon: React.ReactNode; message: string }> = ({ icon, message }) => (
  <div className="flex flex-col items-center justify-center gap-2 px-4 py-14 text-center">
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400">{icon}</span>
    <p className="text-sm text-gray-500">{message}</p>
  </div>
);
